import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import FedaPayRemboursementButton from '../UI/FedaPayRemboursementButton';
import { BACKEND_URL } from '../../config/backend';
import { supabase } from '../../utils/supabaseClient';
import { getLoans, getPayments } from '../../utils/supabaseAPI';
import {
  ArrowLeft, Wallet, AlertTriangle, CreditCard,
  Calendar, TrendingUp, CheckCircle, DollarSign
} from 'lucide-react';
import { formatCurrency } from '../../utils/helpers';

const NAVY = '#0f2d6b';
const GOLD = '#e8a020';

const Repayment = () => {
  const { showSuccess, showError } = useNotifications();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentLoan, setCurrentLoan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentHistory, setPaymentHistory] = useState([]);

  // ── Functional logic (unchanged) ────────────────────────────────────────────
  const loadActiveLoan = async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      const [loansResult, paymentsResult] = await Promise.all([getLoans(user.id), getPayments(user.id)]);
      if (loansResult.success && paymentsResult.success) {
        const loans    = loansResult.data || [];
        const payments = paymentsResult.data || [];
        const activeLoan = loans.find(l => l.status === 'active' || l.status === 'approved' || l.status === 'overdue');
        if (activeLoan) {
          const loanPayments = payments.filter(p => p.loan_id === activeLoan.id);
          const paidAmount   = loanPayments.reduce((s, p) => s + (p.amount || 0), 0);
          const principalAmount = parseFloat(activeLoan.amount);
          const interestAmount  = principalAmount * (activeLoan.interest_rate || 0) / 100;
          const totalOriginalAmount = principalAmount + interestAmount;
          let durationDays = 30;
          if (activeLoan.duration_months != null) durationDays = Number(activeLoan.duration_months);
          else if (activeLoan.duration != null)   durationDays = Number(activeLoan.duration);
          let dueDate = null, nextPaymentDate = null;
          if (activeLoan.approved_at) {
            const approvedDate = new Date(activeLoan.approved_at);
            dueDate = new Date(approvedDate);
            dueDate.setDate(dueDate.getDate() + durationDays);
          }
          let penaltyAmount = parseFloat(activeLoan.total_penalty_amount || 0);
          let daysOverdue   = 0;
          const isOverdue   = activeLoan.status === 'overdue';
          if (dueDate) {
            const today = new Date(); today.setHours(0,0,0,0);
            const dueDateOnly = new Date(dueDate); dueDateOnly.setHours(0,0,0,0);
            daysOverdue = Math.max(0, Math.floor((today - dueDateOnly) / (1000 * 60 * 60 * 24)));
            // Pénalité calculée uniquement sur la base des dates réelles (jamais fabriquée).
            if (daysOverdue > 0 && penaltyAmount === 0) {
              const rate = parseFloat(activeLoan.daily_penalty_rate) || 2.0;
              const periods = Math.floor(daysOverdue / 5);
              if (periods > 0) penaltyAmount = totalOriginalAmount * Math.pow(1 + rate/100, periods) - totalOriginalAmount;
            }
          }
          if (activeLoan.approved_at) {
            const approvedDate = new Date(activeLoan.approved_at);
            const now = new Date();
            const daysSince = Math.floor((now - approvedDate) / (1000 * 60 * 60 * 24));
            const nextDay   = daysSince + 30 - (daysSince % 30);
            nextPaymentDate = new Date(approvedDate.getTime() + nextDay * 24 * 60 * 60 * 1000);
          }
          const totalAmountWithPenalty = totalOriginalAmount + penaltyAmount;
          const remainingAmount        = Math.max(0, totalAmountWithPenalty - paidAmount);
          const formattedLoan = {
            id: activeLoan.id, amount: activeLoan.amount,
            monthlyPayment: activeLoan.monthly_payment || Math.round(totalOriginalAmount / durationDays),
            totalAmount: Math.round(totalOriginalAmount),
            paidAmount: Math.round(paidAmount),
            remainingAmount: Math.ceil(remainingAmount * 100) / 100,
            penaltyAmount: Math.ceil(penaltyAmount * 100) / 100,
            daysOverdue, totalAmountWithPenalty: Math.ceil(totalAmountWithPenalty * 100) / 100,
            dueDate: dueDate ? dueDate.toISOString().split('T')[0] : null,
            nextPaymentDate: nextPaymentDate ? nextPaymentDate.toISOString().split('T')[0] : null,
            interest_rate: activeLoan.interest_rate || 0,
            duration: durationDays, purpose: activeLoan.purpose || 'Non spécifié', status: activeLoan.status
          };
          if (formattedLoan.remainingAmount <= 1) {
            setCurrentLoan(null);
            setPaymentHistory([]);
            queryClient.invalidateQueries({ queryKey: ['dashboardStats', user?.id] });
          } else {
            setCurrentLoan(formattedLoan);
            setPaymentHistory(loanPayments.sort((a, b) => new Date(b.payment_date) - new Date(a.payment_date)));
          }
        } else {
          setCurrentLoan(null);
        }
      }
    } catch (err) { console.error('[REPAYMENT]', err.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadActiveLoan(); /* eslint-disable-next-line */ }, [user?.id]);

  const handleRepaymentSuccess = () => {
    showSuccess("Remboursement effectué avec succès !");
    queryClient.invalidateQueries({ queryKey: ['dashboardStats', user?.id] });
    setTimeout(() => loadActiveLoan(), 1000);
    if (currentLoan?.id) {
      setTimeout(async () => {
        try {
          const session = await supabase.auth.getSession();
          const token = session.data?.session?.access_token;
          await fetch(`${BACKEND_URL}/api/sync-loan-status`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: JSON.stringify({ loanId: currentLoan.id })
          });
        } catch (e) { /* silencieux */ }
        loadActiveLoan();
      }, 6000);
    }
  };

  const handleRepaymentError = (error) => {
    console.error('[REPAYMENT_ERROR]', error);
    let errorMsg = 'Erreur lors du remboursement';

    if (error?.message) {
      errorMsg = error.message;
    } else if (typeof error === 'string') {
      errorMsg = error;
    } else if (error?.raw) {
      // Cas où c'est un objet d'erreur FedaPay
      errorMsg = error.raw.message || JSON.stringify(error.raw);
    }

    // Nettoie le message d'erreur (enlève les balises HTML, etc.)
    errorMsg = errorMsg.replace(/<[^>]*>/g, '').trim();

    // Affiche dans la console pour debug
    console.error('[REPAYMENT_ERROR] Message affiché:', errorMsg);

    showSuccess(`❌ Erreur de paiement: ${errorMsg.substring(0, 100)}`);
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  // En retard = uniquement si les dates réelles le confirment (jamais sur un statut DB obsolète).
  const isOverdue = currentLoan?.daysOverdue > 0;

  return (
    <div className="min-h-screen bg-slate-50 pb-24">

      {/* ── Hero ── */}
      <div className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #1a3f8f 100%)` }}>
        <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-white/5 pointer-events-none" />
        <div className="absolute -bottom-5 -left-5 w-24 h-24 rounded-full pointer-events-none" style={{ background: `${GOLD}15` }} />

        <div className="relative z-10 max-w-2xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => navigate('/dashboard')}
              className="w-8 h-8 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center flex-shrink-0 transition-colors">
              <ArrowLeft size={16} className="text-white" />
            </button>
            <div>
              <p className="text-white/50 text-[10px] font-medium tracking-wide">AB Campus Finance</p>
              <h1 className="text-white font-bold text-base leading-tight">Remboursement de prêt</h1>
            </div>
          </div>

          {/* Amount hero (only when loan exists) */}
          {!loading && currentLoan && (
            <div className="pb-4">
              <p className="text-white/55 text-xs mb-1">Montant restant à rembourser</p>
              <p className="text-white text-4xl font-extrabold tracking-tight">
                {formatCurrency(currentLoan.remainingAmount)}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${
                  isOverdue ? 'bg-red-500/30 text-red-200' : 'bg-green-500/20 text-green-200'
                }`}>
                  {isOverdue ? `Retard ${currentLoan.daysOverdue}j` : 'Actif'}
                </span>
                <span className="text-white/40 text-xs">·</span>
                <span className="text-white/55 text-xs">{currentLoan.purpose}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Content ── */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-4 space-y-4">

        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: NAVY }} />
          </div>
        )}

        {!loading && currentLoan && (
          <>
            {/* Overdue alert */}
            {currentLoan.penaltyAmount > 0 && (
              <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-2xl px-4 py-3">
                <AlertTriangle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-800 text-sm">Prêt en retard</p>
                  <p className="text-red-600 text-xs mt-0.5">
                    Pénalités de 2% / 5 jours appliquées · {formatCurrency(currentLoan.penaltyAmount)} accumulées
                  </p>
                </div>
              </div>
            )}

            {/* Stats grid */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="grid grid-cols-2 divide-x divide-slate-100">
                <div className="px-4 py-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: '#EFF6FF' }}>
                      <DollarSign size={13} style={{ color: '#2563EB' }} />
                    </div>
                    <span className="text-slate-400 text-xs">Montant initial</span>
                  </div>
                  <p className="text-slate-900 text-lg font-extrabold">{formatCurrency(currentLoan.amount)}</p>
                  <p className="text-slate-400 text-[11px] mt-0.5">Taux {currentLoan.interest_rate}%</p>
                </div>
                <div className="px-4 py-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: '#F0FDF4' }}>
                      <CheckCircle size={13} style={{ color: '#16A34A' }} />
                    </div>
                    <span className="text-slate-400 text-xs">Déjà remboursé</span>
                  </div>
                  <p className="text-slate-900 text-lg font-extrabold">{formatCurrency(currentLoan.paidAmount)}</p>
                  <p className="text-slate-400 text-[11px] mt-0.5">sur {formatCurrency(currentLoan.totalAmount)}</p>
                </div>
              </div>

              {/* Progress bar */}
              <div className="px-4 pb-4 pt-1 border-t border-slate-100">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-slate-400 text-xs">Progression</span>
                  <span className="text-xs font-bold" style={{ color: NAVY }}>
                    {Math.round((currentLoan.paidAmount / currentLoan.totalAmount) * 100)}%
                  </span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-700"
                    style={{
                      width: `${Math.min(100, Math.round((currentLoan.paidAmount / currentLoan.totalAmount) * 100))}%`,
                      background: `linear-gradient(90deg, ${NAVY}, #2563EB)`
                    }} />
                </div>
              </div>
            </div>

            {/* Date & penalty info */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100">
              <div className="grid grid-cols-2 divide-x divide-slate-100">
                <div className="px-4 py-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: '#F5F3FF' }}>
                      <Calendar size={13} style={{ color: '#7C3AED' }} />
                    </div>
                    <span className="text-slate-400 text-xs">Date d'échéance</span>
                  </div>
                  <p className="text-slate-900 text-sm font-bold">
                    {currentLoan.dueDate
                      ? new Date(currentLoan.dueDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
                      : 'Non défini'}
                  </p>
                  <p className="text-slate-400 text-[11px] mt-0.5">Durée : {currentLoan.duration} jours</p>
                </div>
                <div className="px-4 py-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                      style={{ background: currentLoan.penaltyAmount > 0 ? '#FEF2F2' : '#F0FDF4' }}>
                      <AlertTriangle size={13} style={{ color: currentLoan.penaltyAmount > 0 ? '#DC2626' : '#16A34A' }} />
                    </div>
                    <span className="text-slate-400 text-xs">Pénalités</span>
                  </div>
                  <p className="text-sm font-bold" style={{ color: currentLoan.penaltyAmount > 0 ? '#DC2626' : '#16A34A' }}>
                    {currentLoan.penaltyAmount > 0 ? formatCurrency(currentLoan.penaltyAmount) : 'Aucune'}
                  </p>
                  <p className="text-slate-400 text-[11px] mt-0.5">
                    {currentLoan.daysOverdue > 0 ? `${currentLoan.daysOverdue}j de retard` : 'À jour'}
                  </p>
                </div>
              </div>
            </div>

            {/* Remboursement CTA */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${NAVY}12` }}>
                  <TrendingUp size={18} style={{ color: NAVY }} />
                </div>
                <div>
                  <p className="font-bold text-slate-900 text-sm">Effectuer un remboursement</p>
                  <p className="text-slate-400 text-xs">Montant flexible · Paiement sécurisé via FedaPay</p>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Montant à rembourser (FCFA)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min="0"
                      max={currentLoan?.remainingAmount}
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                      placeholder={`Max: ${formatCurrency(currentLoan?.remainingAmount || 0)}`}
                      className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={() => setPaymentAmount(currentLoan?.remainingAmount || '')}
                      className="px-3 py-2 text-xs font-semibold text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50"
                    >
                      Max
                    </button>
                  </div>
                  {paymentAmount && (
                    <p className="text-xs text-slate-500 mt-1">
                      Montant: {formatCurrency(parseInt(paymentAmount) || 0)}
                    </p>
                  )}
                </div>

                <FedaPayRemboursementButton
                  loan={currentLoan}
                  customAmount={paymentAmount ? parseInt(paymentAmount) : null}
                  onSuccess={handleRepaymentSuccess}
                  onError={handleRepaymentError}
                />
              </div>
            </div>

            {/* Historique des remboursements */}
            {paymentHistory.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
                <h3 className="font-bold text-slate-900 mb-4">Historique des remboursements</h3>
                <div className="space-y-2">
                  {paymentHistory.map((payment) => (
                    <div key={payment.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">
                          {formatCurrency(payment.amount || 0)}
                        </p>
                        <p className="text-xs text-slate-500">
                          {payment.payment_date ? new Date(payment.payment_date).toLocaleDateString('fr-FR') : 'Date inconnue'}
                        </p>
                      </div>
                      <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded">
                        ✓ Payé
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Info points */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 px-4 py-4">
              <ul className="space-y-2">
                {[
                  'Remboursement via MTN MoMo ou Moov Money',
                  'Transaction instantanée et sécurisée',
                  'Évitez les pénalités en remboursant avant l\'échéance',
                ].map(t => (
                  <li key={t} className="flex items-center gap-2 text-xs text-slate-500">
                    <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: GOLD }} />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}

        {/* No loan state */}
        {!loading && !currentLoan && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-10 text-center">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: '#F1F5F9' }}>
              <Wallet size={26} className="text-slate-400" />
            </div>
            <h2 className="font-bold text-slate-900 text-base mb-1">Aucun prêt en cours</h2>
            <p className="text-slate-400 text-sm mb-6">Vous n'avez aucun prêt actif à rembourser.</p>
            <button onClick={() => navigate('/loan-request')}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-semibold text-sm transition-opacity hover:opacity-90"
              style={{ background: NAVY }}>
              <CreditCard size={16} />
              Demander un prêt
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default Repayment;
